import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({
    title,
    description,
    image,
    type = 'website',
    keywords,
    schemaType = 'WebPage',
    tool = null,
    category = null,
    article = null, // For blog posts: { author, datePublished, dateModified }
    hreflangs = null // Array of { lang: 'en-US', url: 'https://runmytools.com' }
}) => {
    const location = useLocation();

    // Base URL - update this to your actual domain
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://runmytools.com';
    const normalizedPath = location.pathname.length > 1
        ? location.pathname.replace(/\/+$/, '')
        : location.pathname;
    const canonicalUrl = `${baseUrl}${normalizedPath}`;
    const currentUrl = `${baseUrl}${location.pathname}${location.search}`;

    // Default values
    const defaultTitle = 'Free All-in-One Online Toolbox | runmytools';
    const defaultDescription = 'Free all-in-one online toolbox. Convert text, test typing speed, count words, and more. All your online tools in one place.';
    const defaultImage = `${baseUrl}/logo512.png`;

    const seoTitle = title ? `${title} | ${defaultTitle}` : defaultTitle;
    const seoDescription = description || defaultDescription;
    const seoImage = image || defaultImage;
    const seoKeywords = keywords || 'online tools, free tools, text converter, typing speed test, word counter, case converter, online utilities';

    // Ensure document title updates when location or title changes
    useEffect(() => {
        document.title = seoTitle;
    }, [seoTitle, location.pathname]);

    // Organization Schema (always included)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "runmytools",
        "url": baseUrl,
        "logo": `${baseUrl}/logo512.png`,
        "description": "Free all-in-one online toolbox with various utilities and tools",
        "sameAs": [
            // Add your social media links here when available
            // "https://twitter.com/runmytools",
            // "https://facebook.com/runmytools"
        ]
    };

    // WebSite Schema (for homepage)
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "runmytools",
        "url": baseUrl,
        "description": defaultDescription,
        "publisher": {
            "@type": "Organization",
            "name": "runmytools",
            "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/logo512.png`
            }
        },
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
        }
    };

    // WebPage Schema (for regular pages)
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": seoTitle,
        "description": seoDescription,
        "url": canonicalUrl,
        "inLanguage": "en-US",
        "isPartOf": {
            "@type": "WebSite",
            "name": "runmytools",
            "url": baseUrl
        },
        "about": {
            "@type": "Thing",
            "name": seoTitle
        },
        "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": seoImage
        }
    };

    // SoftwareApplication Schema (for tool pages)
    const softwareApplicationSchema = tool ? {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": tool.name,
        "description": tool.description,
        "url": canonicalUrl,
        "applicationCategory": "UtilityApplication",
        "operatingSystem": "Web Browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.5",
            "ratingCount": "100"
        },
        "isAccessibleForFree": true,
        "inLanguage": "en-US"
    } : null;

    // Article Schema (for blog posts)
    const articleSchema = article ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": seoTitle,
        "description": seoDescription,
        "image": seoImage,
        "datePublished": article.datePublished || new Date().toISOString(),
        "dateModified": article.dateModified || article.datePublished || new Date().toISOString(),
        "author": {
            "@type": "Person",
            "name": article.author || "runmytools"
        },
        "publisher": {
            "@type": "Organization",
            "name": "runmytools",
            "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/logo512.png`
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": currentUrl
        }
    } : null;

    // BreadcrumbList Schema
    const getBreadcrumbSchema = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const breadcrumbs = [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": baseUrl
            }
        ];

        let currentPath = '';
        let position = 2;

        // Handle tool pages specially
        if (location.pathname.startsWith('/tool/') && tool) {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": position,
                "name": tool.name,
                "item": canonicalUrl
            });
        } else {
            // Handle other pages
            pathSegments.forEach((segment, index) => {
                currentPath += `/${segment}`;
                let name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

                // Use category name if available
                if (segment === 'category' && category) {
                    name = category;
                } else if (pathSegments[index - 1] === 'category' && category) {
                    // Skip category ID, use the category name from previous segment
                    return;
                }

                breadcrumbs.push({
                    "@type": "ListItem",
                    "position": position,
                    "name": name,
                    "item": `${baseUrl}${currentPath}`
                });
                position++;
            });
        }

        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs
        };
    };

    // Get the appropriate schema based on page type
    const getSchema = () => {
        const schemas = [organizationSchema];

        if (location.pathname === '/') {
            schemas.push(websiteSchema);
        }

        // Article schema for blog posts
        if (schemaType === 'Article' && articleSchema) {
            schemas.push(articleSchema);
        }
        // SoftwareApplication schema for tool pages
        else if (schemaType === 'SoftwareApplication' && softwareApplicationSchema) {
            schemas.push(softwareApplicationSchema);
        }
        // Default WebPage schema
        else {
            schemas.push(webPageSchema);
        }

        // Add breadcrumbs for non-home pages
        if (location.pathname !== '/') {
            schemas.push(getBreadcrumbSchema());
        }

        return schemas;
    };

    const schemas = getSchema();

    return (
        <Helmet key={location.pathname}>
            {/* Primary Meta Tags */}
            <title>{seoTitle}</title>
            <meta name="title" content={seoTitle} />
            <meta name="description" content={seoDescription} />
            <meta name="keywords" content={seoKeywords} />

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* X-Robots-Tag Meta (fallback if HTTP header not available) */}
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta httpEquiv="X-Robots-Tag" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

            {/* Hreflang Tags for Internationalization */}
            {hreflangs && hreflangs.length > 0 && (
                <>
                    {hreflangs.map((hreflang, index) => (
                        <link
                            key={index}
                            rel="alternate"
                            hrefLang={hreflang.lang}
                            href={hreflang.url}
                        />
                    ))}
                </>
            )}
            {/* Default hreflang for English (US) */}
            {(!hreflangs || hreflangs.length === 0) && (
                <>
                    <link rel="alternate" hrefLang="en" href={canonicalUrl} />
                    <link rel="alternate" hrefLang="en-US" href={canonicalUrl} />
                    <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
                </>
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content={seoImage} />
            <meta property="og:image:width" content="512" />
            <meta property="og:image:height" content="512" />
            <meta property="og:site_name" content="runmytools" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDescription} />
            <meta name="twitter:image" content={seoImage} />

            {/* Schema.org Structured Data */}
            {schemas.map((schema, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
};

export default SEO;

